import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * SELYNE:
 * Mantiene el archivo original del tema, pero agrega un fallback para abrir
 * el carrito lateral cuando el producto se agrega desde GemPages.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {boolean} */
  #externalAtcOpening = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);

    /* SELYNE: abre carrito lateral después de Add to Cart de GemPages */
    document.addEventListener('click', this.#handleGemPagesAddToCart, true);

    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);

    /* SELYNE */
    document.removeEventListener('click', this.#handleGemPagesAddToCart, true);

    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g., the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * SELYNE:
   * GemPages agrega el producto, pero no siempre dispara el evento nativo de Horizon.
   * Por eso escuchamos el click del botón y abrimos el drawer después de que Shopify actualice el carrito.
   *
   * Importante:
   * No usamos preventDefault para no romper GemPages, Kaching Bundles ni Kaching Subscriptions.
   *
   * @param {MouseEvent} event
   */
  #handleGemPagesAddToCart = (event) => {
    if (this.#externalAtcOpening) return;

    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const button = target.closest(
      '.gp-button-atc, .customizer-submit-button, gp-product-button button[type="submit"], button[name="add"]'
    );

    if (!(button instanceof HTMLElement)) return;
    if (button.closest('#cart-drawer')) return;
    if (button.matches('[disabled], [aria-disabled="true"]')) return;

    this.#openDrawerAfterGemPagesAdd();
  };

  /**
   * Espera el add de GemPages, refresca el drawer y lo abre.
   */
  #openDrawerAfterGemPagesAdd = async () => {
    this.#externalAtcOpening = true;

    try {
      const delays = [500, 900, 1400];

      for (const delay of delays) {
        await this.#wait(delay);

        await this.#refreshCartDrawerSection();

        if (!this.#themeDrawer?.isOpen && !this.#isCartEmpty()) {
          this.#themeDrawer?.open();
        }

        requestAnimationFrame(() => this.#updateStickyState());

        if (!this.#isCartEmpty()) {
          break;
        }
      }
    } catch (error) {
      console.warn('[cart-drawer] GemPages drawer open fallback failed:', error);
    } finally {
      window.setTimeout(() => {
        this.#externalAtcOpening = false;
      }, 700);
    }
  };

  /**
   * Refresca solo el contenido interno del cart drawer.
   *
   * @returns {Promise<boolean>}
   */
  #refreshCartDrawerSection = async () => {
    const sectionId = 'cart-drawer-section';

    const urlsToTry = [
      `${window.location.pathname}?sections=${sectionId}&_=${Date.now()}`,
      `/?sections=${sectionId}&_=${Date.now()}`,
    ];

    for (const url of urlsToTry) {
      try {
        const response = await fetch(url, {
          method: 'GET',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
          },
        }).then((res) => res.json());

        const sectionHtml = response?.[sectionId];

        if (!sectionHtml) continue;

        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');

        const currentInner = this.querySelector(
          '.cart-drawer__inner[data-hydration-key="cart-drawer-inner"]'
        );

        const newInner =
          doc.querySelector('.cart-drawer__inner[data-hydration-key="cart-drawer-inner"]') ||
          doc.querySelector('#cart-drawer .cart-drawer__inner') ||
          doc.querySelector('.cart-drawer__inner');

        if (currentInner && newInner) {
          currentInner.className = newInner.className;
          currentInner.innerHTML = newInner.innerHTML;
          return true;
        }
      } catch {
        // Try next URL.
      }
    }

    return false;
  };

  /**
   * @param {number} ms
   * @returns {Promise<void>}
   */
  #wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
